import type { ReactNode, Ref } from "react";
import { Input } from "./Input";
import { cn } from "../lib/cn";
import { useRemoteCombobox } from "../hooks/useRemoteCombobox";

export interface SearchComboboxProps<T> {
  label: string;
  /** Visually hide the label (forwarded to the shared `Input`). */
  labelHidden?: boolean | undefined;
  placeholder?: string | undefined;
  maxLength?: number | undefined;
  /** Accessible name for the dropdown listbox. */
  listboxLabel: string;
  /** Fetch matches for a query (already debounced by the hook). */
  search: (query: string) => Promise<T[]>;
  onSelect: (item: T) => void;
  /** Row renderer; its visible text becomes the option's accessible name. */
  renderItem: (item: T) => ReactNode;
  /** Stable per-item key. */
  getKey: (item: T) => string;
  noMatchesText: string;
  disabled?: boolean | undefined;
  hint?: string | undefined;
  /** The underlying input; the flow moves focus here after an upstream selection. */
  inputRef?: Ref<HTMLInputElement> | undefined;
}

/**
 * The cost tool's remote typeahead for the plan and drug pickers: a shared
 * `Input` wired to `useRemoteCombobox`, with a dropdown like
 * `AddressSuggestionListbox`. The two pickers differ only in copy and row
 * rendering. A lookup failure renders an inline "couldn't search" row, and
 * typing again retries.
 */
export function SearchCombobox<T>({
  label,
  labelHidden,
  placeholder,
  maxLength,
  listboxLabel,
  search,
  onSelect,
  renderItem,
  getKey,
  noMatchesText,
  disabled,
  hint,
  inputRef,
}: SearchComboboxProps<T>): ReactNode {
  const cb = useRemoteCombobox<T>({ search, onSelect, disabled });

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        label={label}
        labelHidden={labelHidden}
        hint={hint}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        value={cb.value}
        autoComplete="off"
        enterKeyHint="search"
        {...cb.comboboxProps}
        onChange={(e) => cb.handleInputChange(e.target.value)}
        onKeyDown={cb.handleKeyDown}
        onBlur={cb.handleBlur}
      />
      {cb.open && (
        <ul
          role="listbox"
          id={cb.listboxId}
          aria-label={listboxLabel}
          className="border-haze absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-md border bg-white py-1 shadow-lg"
        >
          {cb.items.length > 0 ? (
            cb.items.map((item, i) => (
              <li
                key={getKey(item)}
                id={cb.optionId(i)}
                role="option"
                aria-selected={i === cb.activeIndex}
                className={cn(
                  "hover:bg-ice text-foreground cursor-pointer px-4 py-2.5 text-sm",
                  i === cb.activeIndex && "bg-ice",
                )}
                // preventDefault keeps focus in the input so blur doesn't close
                // the dropdown before the click's select fires.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => cb.select(i)}
              >
                {renderItem(item)}
              </li>
            ))
          ) : (
            <li className="text-slate-teal px-4 py-2.5 text-sm">
              {cb.loading
                ? "Searching…"
                : cb.error
                  ? "We couldn't search right now. Keep typing to try again."
                  : noMatchesText}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
